import {
  collection,
  doc,
  getDocFromServer,
  getDocsFromServer,
  query,
  where,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { ref, uploadBytes, getBytes, deleteObject } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from '@/firebase'
import { buildReferralFixture } from '../referralAccessFixtures.js'
import { isWorkspaceRotatorEmail } from '@/utils/workspaceRoleRotator'
import { rotateDemoRoleInSession } from '../roleRotate.js'
import {
  REFERRAL_FIXTURES,
  REFERRAL_PROBE_ROLES,
  studentReadQueryFilters
} from '../referralAccessPlan.js'
import { runReferralAccessWorkflow } from '../referralAccessWorkflow.js'

const FILE_NAME = 'general/smoke-referral.pdf'
const SETUP_ROLES = ['admin', 'district_admin', 'school_admin']
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export function createReferralAccessAdapter(ctx) {
  const uid = ctx.user.uid
  const canRotate = isWorkspaceRotatorEmail(ctx.user.email) && ctx.roleClaim !== 'district_admin'
  const setupRole = SETUP_ROLES.includes(ctx.roleClaim) ? ctx.roleClaim : 'school_admin'
  const otherCm = `smoke-referral-other-${doc(collection(db, 'students')).id}`
  const fixtures = Object.fromEntries(
    REFERRAL_FIXTURES.map((key) => {
      const student = doc(collection(db, 'students'))
      return [
        key,
        {
          student,
          evaluation: doc(collection(db, 'studentEvaluations')),
          share: doc(db, 'extendedAccess', student.id),
          file: ref(storage, `students/${student.id}/${FILE_NAME}`),
          created: false,
          evalCreated: false,
          shareCreated: false,
          fileCreated: false
        }
      ]
    })
  )
  let testingAccess = false
  let touchedRole = false

  async function verifyRole(role) {
    const user = getAuth().currentUser
    if (!user || user.uid !== uid)
      throw new Error('Signed-in account changed during the referral check')
    const token = await user.getIdTokenResult(true)
    if (token.claims.role !== role)
      throw new Error(`Expected real JWT role ${role}; received ${token.claims.role || 'none'}`)
    return `real JWT verified: ${role}`
  }
  async function asRole(role) {
    const user = getAuth().currentUser
    if (!user || user.uid !== uid)
      throw new Error('Signed-in account changed during the referral check')
    const token = await user.getIdTokenResult(true)
    if (token.claims.role !== role) {
      if (!canRotate) throw new Error('This login cannot rotate roles for fixture setup')
      touchedRole = true
      await rotateDemoRoleInSession(role)
    }
    return verifyRole(role)
  }
  async function withSetupRole(operation) {
    try {
      await asRole(setupRole)
      return await operation()
    } finally {
      await asRole(ctx.roleClaim)
    }
  }
  async function inspectDerived(consented) {
    for (const [key, f] of Object.entries(fixtures)) {
      const s = await getDocFromServer(f.student)
      if (!s.exists()) throw new Error(`Missing ${key} fixture`)
      const data = s.data(),
        app = data.app || {},
        sd = app.studentData || {}
      if (
        sd.plan !== (key === 'live504' ? '504' : 'IEP') ||
        sd.planStatus !== (key === 'live504' ? '' : 'initial') ||
        sd.caseManagerId !== (key === 'owned' ? '' : otherCm)
      ) {
        throw new Error(`${key}: source plan or plan case manager unexpectedly changed`)
      }
      const standard = key === 'live504' || consented
      const owner = key === 'owned' ? uid : otherCm
      if (
        app.staffAccess !== standard ||
        !app.referralOwnerIds?.includes(owner) ||
        (app.staffIds || []).includes(uid) !== (standard || key === 'owned') ||
        (app.evalCaseManagerId || '') !== (consented ? owner : '')
      )
        return false
      for (const c of ['extendedAccessStudents', 'testingAccommodations']) {
        const copy = await getDocFromServer(doc(db, c, f.student.id))
        if (!copy.exists() || copy.data().staffAccess !== standard) return false
        if (
          c === 'extendedAccessStudents' &&
          (copy.data().allowedUsers || []).includes(uid) !== standard
        )
          return false
      }
    }
    return true
  }
  async function waitForDerived(consented) {
    const deadline = Date.now() + 75000
    do {
      if (await inspectDerived(consented)) return
      await wait(1500)
    } while (Date.now() < deadline)
    throw new Error('Access worker did not reconcile all source/mirror fields within 75 seconds')
  }

  return {
    blockReason() {
      if (import.meta.env.VITE_FIREBASE_PROJECT_ID !== 'casemangervue')
        return 'Referral fixture writes are limited to hosted dev.'
      if (!REFERRAL_PROBE_ROLES.includes(ctx.roleClaim))
        return 'This role is outside the referral-access test matrix.'
      if (!ctx.schoolId) return 'Select a school before running Referral access.'
      if (!SETUP_ROLES.includes(ctx.roleClaim) && !canRotate)
        return 'Use admin@casemanagevue.com and Complete check to prepare fixtures and test staff roles. This account cannot prepare the test students.'
      if (setupRole === 'school_admin' && !ctx.tokenSchoolIds?.includes(ctx.schoolId))
        return 'The demo login must be assigned to the selected school before preparing fixtures.'
      return null
    },
    testingAccess: () => testingAccess,
    assertRole: () => verifyRole(ctx.roleClaim),
    async prepare() {
      return withSetupRole(async () => {
        const profile = await getDocFromServer(doc(db, 'users', uid))
        testingAccess = profile.exists() && profile.data().testingAccess === true
        for (const [key, f] of Object.entries(fixtures)) {
          const data = buildReferralFixture({
            key,
            uid,
            otherCm,
            schoolId: ctx.schoolId,
            runId: ctx.runId,
            studentId: f.student.id
          })
          // Mark before each request: a lost response can still commit a write.
          f.created = true
          await setDoc(f.student, { ...data.student, createdAt: serverTimestamp() })
          f.evalCreated = true
          await setDoc(f.evaluation, {
            ...data.evaluation,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          })
          f.shareCreated = true
          await setDoc(f.share, data.share)
          f.fileCreated = true
          await uploadBytes(
            f.file,
            new Blob(['%PDF-1.4\nSynthetic referral smoke only.\n%%EOF'], {
              type: 'application/pdf'
            })
          )
        }
        await waitForDerived(false)
        return 'Three tagged students, evaluations, shares and files ready; no existing student or aide schedule changed'
      })
    },
    async setConsent(consented) {
      return withSetupRole(async () => {
        for (const f of Object.values(fixtures)) {
          await updateDoc(f.evaluation, {
            'dates.consent': consented ? '2026-10-28' : '',
            updatedAt: serverTimestamp()
          })
          await httpsCallable(functions, 'refreshStudentAccess')({ studentId: f.student.id })
        }
        // The save callable promises completion, so stale state is a failure.
        if (!(await inspectDerived(consented)))
          throw new Error('Access refresh returned before grants/copies matched consent')
        return 'Consent saved and all access fields verified; active 504 unchanged'
      })
    },
    async read(key, target) {
      const f = fixtures[key]
      if (target === 'student') {
        if (!(await getDocFromServer(f.student)).exists()) throw new Error('Fixture disappeared')
      } else if (target === 'Storage file') {
        if (!(await getBytes(f.file)).byteLength) throw new Error('Empty fixture file')
      } else if (target === 'file URL function') {
        const { data } = await httpsCallable(
          functions,
          'getStudentFileUrl'
        )({ studentId: f.student.id, fileName: FILE_NAME })
        if (!data?.url?.startsWith('https://'))
          throw new Error('File function did not return a signed URL')
      } else if (target === 'legacy download') {
        const token = await getAuth().currentUser.getIdToken()
        const response = await fetch(
          `https://us-central1-casemangervue.cloudfunctions.net/downloadStudentFile/downloadStudentFile?studentId=${f.student.id}&fileName=${encodeURIComponent(FILE_NAME)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: AbortSignal.timeout(30000),
            cache: 'no-store'
          }
        )
        if (!response.ok)
          throw Object.assign(new Error(`Legacy download returned HTTP ${response.status}`), {
            code: `http/${response.status}`
          })
        if (!(await response.arrayBuffer()).byteLength) throw new Error('Empty legacy download')
      } else throw new Error(`Unknown probe target: ${target}`)
    },
    async readCopy(key, collectionName) {
      const snap = await getDocFromServer(doc(db, collectionName, fixtures[key].student.id))
      if (!snap.exists()) throw new Error(`Missing ${collectionName} test copy`)
    },
    async list() {
      const seen = new Set()
      for (const filters of studentReadQueryFilters(ctx.roleClaim, uid)) {
        const snap = await getDocsFromServer(
          query(
            collection(db, 'students'),
            where('schoolId', '==', ctx.schoolId),
            ...filters.map(([field, op, value]) => where(field, op, value))
          )
        )
        snap.docs.forEach((d) => seen.add(d.id))
      }
      return Object.entries(fixtures)
        .filter(([, f]) => seen.has(f.student.id))
        .map(([key]) => key)
    },
    async cleanup() {
      if (!Object.values(fixtures).some((f) => f.created)) return 'No fixtures created'
      return withSetupRole(async () => {
        const errors = []
        const remove = async (label, operation) => {
          try {
            await operation()
          } catch (error) {
            if (error.code !== 'storage/object-not-found')
              errors.push(`${label}: ${error.code || error.message}`)
          }
        }
        for (const f of Object.values(fixtures)) {
          if (f.fileCreated) await remove('file', () => deleteObject(f.file))
          if (f.shareCreated) await remove('share', () => deleteDoc(f.share))
          if (f.evalCreated) await remove('evaluation', () => deleteDoc(f.evaluation))
          if (f.created) await remove('student', () => deleteDoc(f.student))
        }
        if (errors.length)
          throw new Error(
            `Fixture cleanup incomplete: ${errors.join('; ')}. Use Clean up leftovers.`
          )
        return 'Only this scenario’s test files, shares, evaluations and students removed; worker clears derived copies'
      })
    },
    async restore() {
      // Check even if no rotation was needed: another tab can change the role.
      const result = await asRole(ctx.roleClaim)
      return touchedRole ? `Starting role restored; ${result}` : result
    }
  }
}

export default {
  id: 'referralAccess',
  label: 'Referral access — consent on/off and active 504',
  description:
    'Tests student lists, direct reads, shared/testing copies, Storage and both file functions with three disposable students. Use the demo login for staff roles; setup temporarily uses School Admin and restores the tested role. No real student or aide schedule is edited.',
  destructive: true,
  async run(ctx, step) {
    await runReferralAccessWorkflow(ctx, step, createReferralAccessAdapter(ctx))
  }
}
